/* global google */
import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import PropTypes from 'prop-types';

import { addressValidationError } from '../../containers/Widget/actions';
import { cacheFormData } from '../../utils/formCaching/actions';
import { copyFor, capitalization } from '../../copy/copy';
import { parseAddressComponents } from './helpers';

const Container = styled.div`
    position: relative;
    padding: ${props => 12 + props.paddingAdjustment}px 0;

    input {
        -webkit-appearance: none !important;
    }
`;
const Label = styled.label`
    font-family: Averta;
    pointer-events: none;
    line-height: 1.5;
    letter-spacing: 1px;
    padding: 0 5px;
    background-color: white;
    font-weight: ${props => (props.active || props.value ? 600 : 400)};
    color: #979191;
    position: absolute;
    transition: all 250ms ease;
    left: 5px;
    text-transform: ${props =>
        props.active || props.value ? `uppercase` : `capitalize`};
    font-size: ${props => (props.active || props.value ? 8 : 14)}px;
    top: ${props =>
        (props.active || props.value !== '' ? 5 : 20) + props.paddingAdjustment + 'px'};
`;

class ReactGoogleAutocomplete extends React.Component {
    static propTypes = {
        addressValidationError: PropTypes.any,
        bounds: PropTypes.object,
        cacheFormData: PropTypes.func,
        componentRestrictions: PropTypes.object,
        fields: PropTypes.arrayOf(PropTypes.string),
        formName: PropTypes.arrayOf(PropTypes.string),
        change: PropTypes.func,
        onFocus: PropTypes.func,
        onPlaceSelectedCallback: PropTypes.func,
        paddingAdjustment: PropTypes.number,
        setValidState: PropTypes.func,
        types: PropTypes.array,
        typeToReturn: PropTypes.string
    };

    constructor(props) {
        super(props);
        this.autocomplete = null;
        this.event = null;
        this.state = {
            addressSelected: false,
            active: false,
            error: false,
            touched: false,
            value: ''
        };
    }

    componentDidMount() {
        this.setupAutocompleteInputField();
    }

    componentWillUnmount() {
        this.event.remove();
    }

    setupAutocompleteInputField() {
        const { types = ['(cities)'], componentRestrictions, bounds } = this.props;
        const config = { types, bounds };

        if (componentRestrictions) {
            config.componentRestrictions = componentRestrictions;
        }

        this.autocomplete = new google.maps.places.Autocomplete(this.refs.input, config); //eslint-disable-line
        if ((this.props.fields || []).length > 0) {
            this.autocomplete.setFields(this.props.fields);
        }

        this.event = this.autocomplete.addListener(
            'place_changed',
            this.onPlaceSelected.bind(this)
        );
    }

    onPlaceSelected() {
        const place = this.autocomplete.getPlace();
        const fullAddress = parseAddressComponents(
            place.address_components,
            this.props.typeToReturn
        );

        if (!parseAddressComponents(place.address_components, 'object').postalCode) {
            this.setState({
                error: true,
                addressSelected: false,
                value: place.formatted_address
            });
            this.props.setValidState(false);
            this.props.addressValidationError(
                copyFor('validationErrors', 'googleAddress')
            );
            return;
        } else {
            this.setState({
                error: false,
                addressSelected: true,
                fullAddress,
                value: place.formatted_address
            });
            this.props.setValidState(true);
        }

        if (this.props.onPlaceSelectedCallback) {
            this.props.onPlaceSelectedCallback(place);
        }

        // used for storing address fields individually when address exists as a part of pre-existing form
        if (this.props.typeToReturn === 'object') {
            if (this.props.formName) {
                this.props.formName.forEach(name => {
                    for (let field in fullAddress) {
                        this.props.change(name, field, fullAddress[field]);
                    }
                });
            }
            // cache values for later use
            this.props.cacheFormData({ ...fullAddress });
        } else {
            // used for storting address as a string on its own form when not a part of a redux-form set
            if (this.props.formName) {
                this.props.formName.forEach(name =>
                    this.props.change(name, 'address', fullAddress)
                );
            }
            // cache for later use
            this.props.cacheFormData({ fullAddress });
        }
    }

    handleBlur() {
        this.setState({ active: false });
        if (!this.state.addressSelected && !this.state.error) {
            this.setState({ error: true });
            this.props.setValidState(false);
        } else if (this.state.addressSelected) {
            this.setState({ error: false });
            this.props.setValidState(true);
        }
    }

    handleChange(e) {
        this.setState({ value: e.target.value });
        if (e.target.value === '') {
            this.setState({ addressSelected: false });
        }
    }

    handleFocus() {
        this.setState({ active: true, touched: true });
        if (this.props.onFocus) {
            this.props.onFocus();
        }
    }

    render() {
        return (
            <Container paddingAdjustment={this.props.paddingAdjustment || 0}>
                <Label
                    id="google_autocomplete__label"
                    className={this.state.error && !this.state.active && 'error'}
                    error={this.state.error}
                    value={this.state.value}
                    active={this.state.active}
                    paddingAdjustment={this.props.paddingAdjustment || 0}
                >
                    {!this.state.error
                        ? copyFor(
                              'formFields',
                              'fullAddress',
                              'general',
                              capitalization.eachWord
                          )
                        : copyFor(
                              'validationErrors',
                              'fullAddress',
                              'general',
                              capitalization.eachWord
                          )}
                </Label>
                <input
                    // STYLES SET IN GLOBAL - STYLED COMPONENTS BREAK AUTOCOMPLETE
                    ref="input" //eslint-disable-line
                    autoComplete={false}
                    className={`fs-block ${this.state.error &&
                        !this.state.active &&
                        'autocompleteError'}`}
                    id="google_autocomplete"
                    name="fullAddressInput"
                    onBlur={() => this.handleBlur()}
                    onChange={e => this.handleChange(e)}
                    onFocus={() => this.handleFocus()}
                    placeholder=""
                    type="text"
                    value={this.state.value}
                />
            </Container>
        );
    }
}

export default connect(
    () => ({
        true: true // to suppress mapStatesToProps error
    }),
    {
        addressValidationError,
        cacheFormData,
        change
    }
)(ReactGoogleAutocomplete);
